import { useRef, useState } from 'react';
import { FiFile, FiImage, FiUpload, FiX } from 'react-icons/fi';

// Upload do comprovante de matrícula: aceita PDF ou imagem (jpg/png/webp).
// Devolve { arquivo: base64DataURI, mimeType } via onChange. Sem cropping nem
// compressão — o operador/aluno escolhe um arquivo e a gente armazena como
// está. Tamanho limite (default 5 MB) protege a coluna LONGTEXT.
const MIMES_PERMITIDOS = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

export default function ComprovanteUpload({
    value,           // { arquivo, mimeType } | null
    onChange,        // ({ arquivo, mimeType }) => void  (ou null pra limpar)
    maxMB = 5,
    disabled = false,
}) {
    const inputRef = useRef(null);
    const [erro, setErro] = useState(null);
    const [nome, setNome] = useState(null);

    const escolher = () => { if (!disabled) inputRef.current?.click(); };

    const tratarArquivo = (file) => {
        if (!file) return;
        if (!MIMES_PERMITIDOS.includes(file.type)) {
            setErro('Use PDF, JPG, PNG ou WEBP.');
            return;
        }
        if (file.size > maxMB * 1024 * 1024) {
            setErro(`Arquivo muito grande (máximo ${maxMB} MB).`);
            return;
        }
        setErro(null);
        const reader = new FileReader();
        reader.onload = () => {
            onChange({ arquivo: reader.result, mimeType: file.type });
            setNome(file.name);
        };
        reader.onerror = () => setErro('Erro ao ler o arquivo.');
        reader.readAsDataURL(file);
    };

    const limpar = (e) => {
        e.stopPropagation();
        onChange(null);
        setNome(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const ehPdf = value?.mimeType === 'application/pdf';
    const ehImagem = value?.mimeType?.startsWith('image/');

    return (
        <div>
            <div
                onClick={escolher}
                style={{ ...styles.drop, opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
                {!value && (
                    <div style={styles.placeholder}>
                        <FiUpload size={22} />
                        <div style={styles.placeholderText}>
                            Clique para anexar seu comprovante
                        </div>
                        <div style={styles.placeholderHint}>PDF, JPG ou PNG (até {maxMB} MB)</div>
                    </div>
                )}
                {value && ehImagem && (
                    <div style={styles.previewWrap}>
                        <img src={value.arquivo} alt="Comprovante" style={styles.previewImg} />
                        <button type="button" onClick={limpar} style={styles.removerBtn} title="Remover">
                            <FiX size={14} />
                        </button>
                    </div>
                )}
                {value && ehPdf && (
                    <div style={styles.previewPdf}>
                        <FiFile size={26} color="#DC2626" />
                        <div style={styles.previewPdfTexto}>
                            <div style={styles.previewPdfNome}>{nome || 'Comprovante.pdf'}</div>
                            <div style={styles.previewPdfBadge}>PDF anexado</div>
                        </div>
                        <button type="button" onClick={limpar} style={styles.removerBtnInline} title="Remover">
                            <FiX size={14} />
                        </button>
                    </div>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => tratarArquivo(e.target.files?.[0])}
                disabled={disabled}
            />
            {erro && <div style={styles.erro}>{erro}</div>}
        </div>
    );
}

const styles = {
    drop: {
        border: '1.5px dashed #CBD5E1', borderRadius: 10, padding: 16,
        background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 110, position: 'relative', transition: 'border-color 0.15s',
    },
    placeholder: {
        textAlign: 'center', color: '#64748B', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 4,
    },
    placeholderText: { fontSize: '0.88rem', fontWeight: 600, color: '#475569' },
    placeholderHint: { fontSize: '0.72rem', color: '#94A3B8' },
    previewWrap: { position: 'relative', display: 'inline-block' },
    previewImg: { maxHeight: 140, maxWidth: '100%', borderRadius: 8, display: 'block' },
    removerBtn: {
        position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: '50%',
        border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    previewPdf: {
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        background: '#fff', padding: '8px 10px', borderRadius: 8, border: '1px solid #FECACA',
    },
    previewPdfTexto: { flex: 1, minWidth: 0 },
    previewPdfNome: {
        fontSize: '0.85rem', fontWeight: 600, color: '#0F172A',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    previewPdfBadge: { fontSize: '0.7rem', color: '#DC2626', fontWeight: 600 },
    removerBtnInline: {
        width: 24, height: 24, borderRadius: '50%', border: 'none', background: '#EF4444',
        color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    erro: {
        marginTop: 6, color: '#9F1239', fontSize: '0.78rem',
        background: '#FFF1F2', padding: '6px 10px', borderRadius: 6, border: '1px solid #FECDD3',
    },
};
